//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useLayoutEffect, useState, useRef, useContext, useCallback } from 'react';
// import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { VerifiedUserRounded } from '@material-ui/icons';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { isAtBottomByContentDimensions } from '../../common/scrollHelper';
import { getUniqueKey } from '../../common/arrayHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { loadThreadDetail, loadThreadMessages, addThreadMessage } from '../../common/services/myMessagesServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdFab from '../../common/elemental/AfitdFab';
import AfitdBadgeCount from '../../common/elemental/AfitdBadgeCount';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ThreadMenuButtons from './ThreadMenuButtons';
import NewMessageSign from './NewMessageSign';
import MessagesDate from './MessagesDate';
import MessageBlockOut from './MessageBlockOut';
import MessageBlockIn from './MessageBlockIn';
import MessageReply from './MessageReply';
import ThreadDetailEmpty from './ThreadDetailEmpty';
import ThreadDetailNoThreads from './ThreadDetailNoThreads';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _throttle from 'lodash/throttle';
import _trim from 'lodash/trim';
import { startOfDay, isEqual, /*parse as parseDate,*/ isAfter } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        threadDetailContainer: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        chatRightBlock1: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '24px',
            borderBottom: '1px solid #f1f2f6',
            [theme.breakpoints.down('sm')]: { paddingLeft: 10 },
        },
        threadPaneHeader: {
            [theme.breakpoints.down('sm')]: {
                display: 'flex',
                alignItems: 'center',
            },
        },
        subjectText: {
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '22px',
        },
        chatRightBlock2: {
            position: 'relative',
            flexGrow: 1,
            padding: '21px 23px 0 23px',
            overflowY: 'hidden',
        },
        chatRightBlock3: {
            padding: '48px 0 21px 0',
            [theme.breakpoints.down('sm')]: { padding: '10px 0 10px 0' },
        },
        companyNameContainer: {
            display: 'flex',
        },
        verifyIcon: {
            fontSize: '18px',
            color: '#1D8A8B',
            margin: '2px 5px',
        },
        hidden: {
            display: 'none',
        },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
            marginRight: 16,
        },
        navIconLeft: { marginLeft: 11, width: 18 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        userId,
        threadData,
        threadMessages = [],
        lastReadDtRef,
        reportedDt,
        markedForFollowUpDt,
        blockedDt,
        // messageListContainerEl,
        messageListEl,
        isAtThreadBottom,
        messagesCountWhenScrolledUp,
    } = params;
    const { setThreadMessages, setSnackbarMessage, setSnackbarErrorMessage, setIsRequestScrollToBottomRef, setNewMessageSignRef } = params;
    const { threadId } = props;
    const { threadDetailContainer: threadDetailContainerCls, hidden: hiddenCls } = classes;

    const lastReadDt = _get(lastReadDtRef, 'current');

    const companyName = _get(threadData, 'companyName');
    const subject = _get(threadData, 'jobTitle');
    const employerProfileImageUrl = _get(threadData, 'companyProfilePicUrl');

    const contentWrapperEl = _get(messageListEl, 'current.contentWrapperEl');
    const contentEl = _get(messageListEl, 'current.contentEl');
    const bottomNewMessagesCount = threadMessages.length - _get(messagesCountWhenScrolledUp, 'current', threadMessages.length);

    const defns = {
        threadDetailContainer: {
            className: cx(threadDetailContainerCls, { [hiddenCls]: !threadId }),
        },
        threadMenuButtons: {
            threadId,
            reportedDt,
            markedForFollowUpDt,
            blockedDt,
        },
        companyNameText: {
            variant: 'body2',
            clsMidGrayColor: true,
            value: companyName,
        },
        subjectText: {
            value: subject,
        },
        messageList: {
            userId,
            threadMessages,
            lastReadDt,
            senderProfileImageUrl: employerProfileImageUrl,
            newMessageSignRef: setNewMessageSignRef,
        },
        messageReply: {
            disabled: !!blockedDt,
            onMessageSend: handleSendMessage.bind(
                null,
                userId,
                threadId,
                threadMessages,
                setThreadMessages,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                companyName,
                lastReadDtRef,
            ),
            onFileUploadComplete: handleCompleteFileUpload.bind(
                null,
                userId,
                threadId,
                threadMessages,
                setThreadMessages,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                companyName,
                lastReadDtRef,
            ),
            setIsRequestScrollToBottomRef,
        },
        goToBottomButton: {
            isHidden: isAtThreadBottom || threadMessages.length === 0,
            size: 'small',
            style: {
                position: 'absolute',
                right: 30,
                bottom: 10,
            },
            onClick: handleClickGoToBottomButton.bind(null, contentWrapperEl, contentEl, messagesCountWhenScrolledUp),
        },
        bottomNewMessagesCount: {
            className: cx({ [hiddenCls]: isAtThreadBottom || bottomNewMessagesCount <= 0 }),
            style: {
                position: 'absolute',
                right: 55,
                bottom: 35,
                zIndex: 1,
            },
        },
        bottomNewMessagesCountText: {
            count: bottomNewMessagesCount,
        },
        noThreadSelectedContainer: {},
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickGoToBottomButton = (contentWrapperEl, contentEl, messagesCountWhenScrolledUp) => {
    contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
    messagesCountWhenScrolledUp.current = undefined;
};

/**
 * MessageList component that iterates through the messages, determine whether it needs to render the Date, the New Messages sign,
 * and message component according to the type of messages (incoming or outgoing).
 *
 * @param {*} props
 */
const MessageList = props => {
    const { userId, threadMessages = [], lastReadDt, senderProfileImageUrl, newMessageSignRef } = props;
    // const totalMessagesCount = threadMessages.length;
    let messageDate = new Date();
    let isNewMessageSignAlreadyDisplayed = false;
    let unreadMessagesCount = 0;

    unreadMessagesCount = _reduce(
        threadMessages,
        (count, message) => {
            const { senderId, createDt } = message;
            if (isAfter(createDt, lastReadDt) && senderId !== userId) {
                return count + 1;
            } else {
                return count;
            }
        },
        0,
    );

    const getMessageList = () =>
        _map(threadMessages, (message, idx) => {
            const { senderId, content, createDt, attachment } = message;

            // const isAttachment = !!attachment;
            let isDisplayDate = false;
            let isShowNewMessageSign = false;
            const isFromEmployer = senderId !== userId; // either is true if from employer: jobSeekerId is populated OR senderId is not userId

            const messageDefn = {
                content,
                createDt,
                senderProfileImageUrl,
                attachment,
                key: `message-${idx}`,
            };

            if (createDt) {
                const createDate = startOfDay(createDt);
                if (!isEqual(createDate, messageDate)) {
                    isDisplayDate = true;
                }
                messageDate = createDate;

                if (unreadMessagesCount > 0 && isFromEmployer && isAfter(createDt, lastReadDt) && !isNewMessageSignAlreadyDisplayed) {
                    isShowNewMessageSign = true;
                    isNewMessageSignAlreadyDisplayed = true;
                }
            }

            const messageComponents = [];
            if (isDisplayDate) {
                const dateDefn = { messageDate, key: `message-date-${getUniqueKey()}` };
                messageComponents.push(<MessagesDate {...dateDefn} />);
            }

            if (isShowNewMessageSign) {
                messageComponents.push(
                    <NewMessageSign unreadMessagesCount={unreadMessagesCount} newMessageSignRef={newMessageSignRef} key={`new-message-sign-${getUniqueKey()}`} />,
                );
            }

            if (senderId === userId) {
                messageComponents.push(<MessageBlockOut {...messageDefn} />);
            } else {
                messageComponents.push(<MessageBlockIn {...messageDefn} />);
            }

            return messageComponents;
        });

    return <div>{getMessageList()}</div>;
};

const setLoadThreadDetailUnsubscribeRef = (ref, unsubscribe) => {
    ref.current = unsubscribe;
};
const detachThreadDetailListener = ref => {
    if (ref.current) {
        // call unsubscribe
        ref.current();
    }
};

const setLoadThreadMessagesUnsubscribeRef = (ref, unsubscribe) => {
    ref.current = unsubscribe;
};
const detachThreadMessagesListener = ref => {
    if (ref.current) {
        // call unsubscribe
        ref.current();
    }
};

/**
 * Call the db function to send the message.
 *
 * @param {*} userId
 * @param {*} threadId
 * @param {*} threadMessages
 * @param {*} setThreadMessages
 * @param {*} setSnackbarMessage
 * @param {*} companyName
 * @param {*} message
 * @param {*} evt
 */
const handleSendMessage = (userId, threadId, threadMessages, setThreadMessages, setSnackbarMessage, setSnackbarErrorMessage, companyName, lastReadDtRef, message, evt) => {
    const trimmedMessage = _trim(message);

    // append new reply message to the messages array in local state
    let messageState = {
        createDt: new Date(),
        senderId: userId,
        content: trimmedMessage,
    };
    setThreadMessages([...threadMessages, messageState]);

    // add new reply message to db
    let messageDto = {
        createDt: new Date(),
        senderId: userId,
        threadId,
        content: trimmedMessage,
    };
    addThreadMessage({ messageDto, setSnackbarMessage, setSnackbarErrorMessage, recipientName: companyName, lastReadDtRef });
};

const handleCompleteFileUpload = (
    userId,
    threadId,
    threadMessages,
    setThreadMessages,
    setSnackbarMessage,
    setSnackbarErrorMessage,
    companyName,
    lastReadDtRef,
    message,
    filename,
    fileType,
    fileSize,
    fileUrl,
    evt,
) => {
    // append new reply attachment and/or message to the messages array in local state
    let messageState = {
        createDt: new Date(),
        senderId: userId,
        attachment: {
            title: filename,
            size: fileSize,
            type: fileType,
            url: fileUrl,
        },
    };
    if (message) {
        _set(messageState, 'content', message);
    }
    setThreadMessages([...threadMessages, messageState]);

    // add new reply attachment and/or message to db
    let messageDto = {
        createDt: new Date(),
        senderId: userId,
        threadId,
        attachment: {
            title: filename,
            size: fileSize,
            type: fileType,
            url: fileUrl,
        },
    };
    if (message) {
        _set(messageDto, 'content', message);
    }
    addThreadMessage({ messageDto, setSnackbarMessage, setSnackbarErrorMessage, recipientName: companyName, lastReadDtRef });
};

/**
 * Figure out if it is at the bottom of the thread.
 *
 * @param {*} lastContentDimensionsRef
 * @param {*} setIsAtThreadBottom
 * @param {*} evt
 */
const handleScrollMessageListEl = (lastContentDimensionsRef, setIsAtThreadBottom, evt) => {
    const contentWrapperEl = evt.target;
    const lastContentDimensions = _get(lastContentDimensionsRef, 'current');
    if (contentWrapperEl) {
        const isAtBottom = isAtBottomByContentDimensions({ containerEl: contentWrapperEl, contentDimensions: lastContentDimensions });
        setIsAtThreadBottom(isAtBottom);
    }
};

/**
 * Throttled the onScroll handler.
 */
const getHandleScrollMessageListEl = _throttle(handleScrollMessageListEl, 500, { leading: true });

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
/**
 * This is how the FAB and the FAB new messages count work:
 * - The scroll spy uses the isAtThreadBottom local state to indicate whether it is at the thread bottom,
 * - The messagesCountWhenScrolledUp ref is undefined initially and when it is at the thread bottom (every time it scroll to bottom it will set messagesCountWhenScrolledUp to undefined).
 * - The side effect with dependencies isAtThreadBottom and threadMessages checks if just scrolling up from the bottom (isAtThreadBottom is false and messagesCountWhenScrolledUp is undefined),
 *   and if so, it sets the messagesCountWhenScrolledUp to the current message count to remember that for later.
 * - When a new message comes in, it renders and getDefns() checks if it is not at the thread bottom (isAtThreadBottom is false) and if so, it displays the FAB;
 *   it also displays the new messages count (latest messages count minus messagesCountWhenScrolledUp).
 * NOTE: The FAB new messages count does not include the new messages before user scrolled up.
 *
 * @param {*} props
 */
const ThreadDetail = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const context = useContext(LayoutContext);
    const { userId, setCurrentThreadId, setSnackbarMessage, setSnackbarErrorMessage } = context;
    const threadIdRef = useRef();
    const lastReadDtRef = useRef();
    const loadThreadDetailUnsubscribeRef = useRef();
    const loadThreadMessagesUnsubscribeRef = useRef();
    const messageListEl = useRef();
    const lastContentDimensionsRef = useRef();
    const isRequestInitialScrollRef = useRef(false);
    const isRequestScrollToBottomRef = useRef(false);
    const newMessageSignRef = useRef();
    const messagesCountWhenScrolledUp = useRef();

    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [threadData, setThreadData] = useState();
    const [threadMessages, setThreadMessages] = useState([]);
    const [isAtThreadBottom, setIsAtThreadBottom] = useState();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { myMessagesId, threadId, lastReadDt, deleteDt, setShowMessageDetails, threadCount } = props;

    //---------------------------------------------------------------------
    // useCallback hook
    //---------------------------------------------------------------------
    const setIsRequestScrollToBottomRef = useCallback(
        value => {
            isRequestScrollToBottomRef.current = value;
        },
        [isRequestScrollToBottomRef],
    );
    const setNewMessageSignRef = useCallback(
        ref => {
            newMessageSignRef.current = ref;
        },
        [newMessageSignRef],
    );

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isEmpBadge = _get(threadData, 'isEmpBadge');
    const reportedDt = _get(threadData, 'reportedDt');
    const markedForFollowUpDt = _get(threadData, 'followUpDt');
    const blockedDt = _get(threadData, 'blockedDt');

    const contentWrapperEl = _get(messageListEl, 'current.contentWrapperEl');
    const contentEl = _get(messageListEl, 'current.contentEl');

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const clearThreadDetail = useCallback(() => {
        setThreadData(null);
        setThreadMessages([]);
        threadIdRef.current = undefined;
        lastReadDtRef.current = undefined;
        messagesCountWhenScrolledUp.current = undefined;
    }, []);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        // only call db only if it is a different thread Id
        if (threadId && threadId !== threadIdRef.current) {
            // clear some states and ref first
            messagesCountWhenScrolledUp.current = undefined;

            // load the thread document
            detachThreadDetailListener(loadThreadDetailUnsubscribeRef);
            const unsubscribeLoadThreadDetailOnSnapshot = loadThreadDetail({
                viewerId: userId,
                myMessagesId,
                threadId,
                setThreadData,
                setSnackbarErrorMessage,
            });
            setLoadThreadDetailUnsubscribeRef(loadThreadDetailUnsubscribeRef, unsubscribeLoadThreadDetailOnSnapshot);

            detachThreadMessagesListener(loadThreadMessagesUnsubscribeRef);
            const unsubscribeLoadThreadMessagesOnSnapshot = loadThreadMessages({
                viewerId: userId,
                myMessagesId,
                threadId,
                deleteDt,
                setThreadMessages,
                setSnackbarErrorMessage,
            });
            setLoadThreadMessagesUnsubscribeRef(loadThreadMessagesUnsubscribeRef, unsubscribeLoadThreadMessagesOnSnapshot);

            // remember about the loaded thread so we can check and prevent calling db or changing lastReadDt on every prop change
            threadIdRef.current = threadId;
            lastReadDtRef.current = lastReadDt;

            // set the current thread Id
            setCurrentThreadId(threadId);
            isRequestInitialScrollRef.current = true;

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates([unsubscribeLoadThreadDetailOnSnapshot, unsubscribeLoadThreadMessagesOnSnapshot]);
            };
        } else if (!threadId) {
            // clear thread detail
            clearThreadDetail();
        }
    }, [userId, myMessagesId, threadId, lastReadDt, deleteDt, setCurrentThreadId, messageListEl, clearThreadDetail, setSnackbarErrorMessage]);

    useLayoutEffect(() => {
        // once messageListEl is available, set up scroll event listener
        if (contentWrapperEl) {
            contentWrapperEl.addEventListener('scroll', getHandleScrollMessageListEl.bind(null, lastContentDimensionsRef, setIsAtThreadBottom));
        }

        // remove scroll event listener when unmount
        return () => {
            if (contentWrapperEl) {
                contentWrapperEl.removeEventListener('scroll', getHandleScrollMessageListEl.bind(null, lastContentDimensionsRef, setIsAtThreadBottom));
            }
        };
    }, [contentWrapperEl, lastContentDimensionsRef]);

    useEffect(() => {
        // if scrolling up from the bottom and messagesCountWhenScrolledUp has not yet been set
        if (isAtThreadBottom === false && _get(messagesCountWhenScrolledUp, 'current') === undefined) {
            // remember the messages count at that time
            messagesCountWhenScrolledUp.current = threadMessages.length;
        } else if (isAtThreadBottom === true) {
            messagesCountWhenScrolledUp.current = undefined;
        }
    }, [isAtThreadBottom, threadMessages]);

    useEffect(() => {
        const isRequestInitialScroll = _get(isRequestInitialScrollRef, 'current');
        const isRequestScrollToBottom = _get(isRequestScrollToBottomRef, 'current');
        const lastContentDimensions = _get(lastContentDimensionsRef, 'current');

        // Scroll to message
        if (contentWrapperEl && contentEl) {
            if (isRequestInitialScroll) {
                // For initial scroll, scroll to first new message or if no new message, scroll to bottom
                const newMessageSignEl = _get(newMessageSignRef, 'current');
                if (newMessageSignEl) {
                    contentWrapperEl.scrollTop = newMessageSignEl.offsetTop; // scroll to first new message
                } else {
                    contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
                    messagesCountWhenScrolledUp.current = undefined;
                }
                isRequestInitialScrollRef.current = false; // reset flag
            } else if (isRequestScrollToBottom || isAtBottomByContentDimensions({ containerEl: contentWrapperEl, contentDimensions: lastContentDimensions })) {
                // If requested by replying messages OR if it was already at the bottom of the thread before new messages arrived, scroll to the bottom now
                contentWrapperEl.scrollTop = contentEl.offsetHeight; // scroll to bottom
                isRequestScrollToBottomRef.current = false; // reset flag
                messagesCountWhenScrolledUp.current = undefined;
            }
            lastContentDimensionsRef.current = contentEl.getBoundingClientRect(); // update content dimension ref
        }
    }, [threadMessages, contentWrapperEl, contentEl]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        userId,
        threadData,
        threadMessages,
        setThreadMessages,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        lastReadDtRef,
        messageListEl,
        reportedDt,
        markedForFollowUpDt,
        blockedDt,
        setIsRequestScrollToBottomRef,
        setNewMessageSignRef,
        isAtThreadBottom,
        messagesCountWhenScrolledUp,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.threadDetailContainer}>
                <div {...defns.chatRightBlock1}>
                    <div {...defns.threadPaneHeader}>
                        {smMatches ? (
                            <div {...defns.navigationIconContainer} onClick={() => setShowMessageDetails(false)}>
                                <ArrowBackIosIcon {...defns.navIconLeft} />
                            </div>
                        ) : null}
                        <div>
                            <div {...defns.companyNameContainer}>
                                <AfitdTypography {...defns.companyNameText} />
                                {isEmpBadge && <VerifiedUserRounded {...defns.verifyIcon} />}
                            </div>
                            <AfitdTypography {...defns.subjectText} />
                        </div>
                    </div>
                    <div {...defns.moreIconButtonContainer}>
                        <ThreadMenuButtons {...defns.threadMenuButtons} />
                    </div>
                </div>
                <div {...defns.chatRightBlock2}>
                    <SimpleBar style={{ height: '100%' }} ref={messageListEl}>
                        <MessageList {...defns.messageList} />
                    </SimpleBar>
                    <div {...defns.bottomNewMessagesCount}>
                        <AfitdBadgeCount {...defns.bottomNewMessagesCountText} />
                    </div>
                    <AfitdFab aria-label="Go to Bottom" {...defns.goToBottomButton}>
                        <KeyboardArrowDown />
                    </AfitdFab>
                </div>
                <div {...defns.chatRightBlock3}>
                    <MessageReply {...defns.messageReply} />
                </div>
            </div>
            {threadCount === 0 && <ThreadDetailNoThreads />}
            {threadCount > 0 && !threadId && <ThreadDetailEmpty />}
        </div>
    );
};

export default ThreadDetail;
